<template>
  <div
    class="wrn-bg-white card full"
    :class="[hasBackground ? 'wrn-color-white has-image' : 'wrn-color-black']"
    v-on="$listeners"
    @mousemove="cardMovement"
    @mouseleave="cardReset"
  >
    <div v-if="hasBackground" class="background-wrapper">
      <div class="overlay full" />
      <div class="glare" />
      <div class="background full" :style="computedBackground">
        <slot name="background" />
      </div>
    </div>
    <div class="card-content full">
      <div class="top-wrapper">
        <slot name="top" />
      </div>
      <div class="center-wrapper">
        <slot name="center" />
      </div>
      <div class="bottom-wrapper">
        <slot name="bottom" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop } from 'vue-property-decorator';
import { CardMovement } from '@/modules/common/mixins/card-movement';

@Component({
  mixins: [CardMovement]
})
export default class BaseCard extends Vue {
  @Prop({ type: String })
  public background!: string;

  private get hasBackground(): boolean {
    return !!this.background || !!this.$slots.background;
  }

  private get computedBackground() {
    if (!this.background) return null;
    return { backgroundImage: `url(${this.background})` };
  }
}
</script>

<style lang="less" scoped>
@easing: cubic-bezier(0.55, 0, 0.1, 1);

.card {
  backface-visibility: hidden;
  border-radius: 14px;
  box-shadow: 0 14px 40px rgba(0, 0, 0, 0.18);
  cursor: pointer;
  display: flex;
  overflow: hidden;
  position: relative;
  transition: transform 200ms cubic-bezier(0.4, 0, 1, 1);
  transform: perspective(800px) translate3d(0, 0, 0);
  transform-style: preserve-3d;
  user-select: none;
  width: 100%;
  height: 100%;
  z-index: 0;

  > * {
    pointer-events: none;
  }

  &.has-image:hover {
    box-shadow: 0 10px 32px rgba(0, 0, 0, 0.35);
  }

  &:hover {
    box-shadow: 0 16px 32px rgba(0, 0, 0, 0.15) !important;
    transition: transform 50ms @easing;
    z-index: 10;

    .overlay {
      opacity: 0.9;
      transition: 0.2s @easing;
    }

    .glare {
      opacity: 0.13;
    }
  }

  &:active {
    opacity: 0.2;
    transform: scale(0.96) !important;
    transition: all 200ms @easing;
  }
}

.full {
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.background {
  background-size: cover;
  background-position: center;
  border-radius: inherit;
  position: absolute;
  z-index: 0;
}

.glare {
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 1) 0%,
    rgba(255, 255, 255, 0) 50%
  );
  backface-visibility: hidden;
  opacity: 0;
  position: absolute;
  top: -50%;
  left: -50%;
  transition: opacity 500ms @easing;
  transform: translate3d(0, 0, 15px);
  width: 200%;
  height: 200%;
  z-index: 10;
}

.overlay {
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.5) 0%,
    rgba(0, 0, 0, 0.15) 70%
  );
  opacity: 1;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.card-content {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 16px;
  position: relative;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 2;

  transform: translate3d(0, 0, 15px);
}

.center-wrapper {
  text-align: center;
}
</style>
